import { useMemo } from 'react';

import { skipToken } from '@apollo/client/react';
import { Sentry } from '@opendoor/observability/sentry';
import { OdProtosSellCommon_Product } from '__generated__/globalTypes';
import { useAuthenticatedQuery } from 'platform/apollo';
import { useAuthAuthenticated } from 'platform/components/AuthContext';
import { hasValue } from 'shared/helpers/utils';

import type {
  ListLeadsByCustomerQuery_seller_OdProtosSellSellerServices_Seller_Query_listLeadsOnUniqueAddresses_OdProtosSellSellerServices_ListLeadsOnUniqueAddressesResponse_leads_OdProtosSellSellerData_Lead as RawSellerCustomerLead,
  ListLeadsByCustomerQuery_seller_OdProtosSellSellerServices_Seller_Query_listLeadsOnUniqueAddresses_OdProtosSellSellerServices_ListLeadsOnUniqueAddressesResponse_leads_OdProtosSellSellerData_Lead_address_OdProtosCasaData_Address as SellerCustomerLeadAddress,
} from './__generated__/useSellerLeadsByCustomer';
import { ListLeadsByCustomerDocument } from './__generated__/useSellerLeadsByCustomer';

export type SellerCustomerLead = Omit<RawSellerCustomerLead, 'address'> & {
  address: SellerCustomerLeadAddress;
};

const isSellerCustomerLead = (
  lead: RawSellerCustomerLead | null | undefined,
): lead is SellerCustomerLead => hasValue(lead?.address);

/**
 * Fetches all of the seller leads for the currently authenticated customer
 */
export const useSellerLeadsByCustomer = () => {
  const { customerUUID } = useAuthAuthenticated();

  const customerLeadsResult = useAuthenticatedQuery(
    ListLeadsByCustomerDocument,
    !customerUUID
      ? skipToken
      : {
          variables: { customerUUID },
          context: { batch: false, linkContext: { team: 'seller' } },
        },
  );

  // Filter out leads that don't have an address uuid
  const customerLeads = useMemo<SellerCustomerLead[]>(() => {
    const leads: SellerCustomerLead[] = [];
    const rawLeads = customerLeadsResult.data?.seller?.listLeadsOnUniqueAddresses?.leads ?? [];

    rawLeads.forEach((lead) => {
      if (isSellerCustomerLead(lead)) {
        leads.push(lead);
      } else {
        Sentry.captureException(
          `useSellerLeadsByCustomer(): Lead ${lead?.id ?? 'unknown'} is missing address UUID`,
        );
      }
    });

    // Only return qualified leads
    return leads.filter((lead) =>
      lead.productOfferings.some(
        (po) => po.product === OdProtosSellCommon_Product.OPENDOOR_DIRECT && po.deniedAt === null,
      ),
    );
  }, [customerLeadsResult.data?.seller?.listLeadsOnUniqueAddresses?.leads]);

  const latestCustomerAddressUUID =
    customerLeads.length > 0 ? customerLeads[customerLeads.length - 1].address?.uuid : undefined;

  return {
    customerLeads: customerLeads,
    latestCustomerAddressUUID,
    loading: customerLeadsResult.loading,
    error: customerLeadsResult.error,
    refetch: customerLeadsResult.refetch,
  };
};
